// assets/motiv-konfigurator.js
// Live-Motiv für die Schlüsselbox (Phase 1): Bild-Upload, Ziehen, Skalieren.
// Reine Funktionen sind DOM-frei und werden mit node --test geprüft.
import { registerSectionLifecycle } from "./motiv-zuschnitt.js";

// Begrenzt eine Position (Prozentwerte) auf 0–100 und rundet auf ganze Prozent.
export function clampPosition({ x, y } = {}) {
  const clamp = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return 50;
    return Math.round(Math.min(100, Math.max(0, num)));
  };
  return { x: clamp(x), y: clamp(y) };
}

export function clampText(value, max = 30) {
  return String(value ?? "").slice(0, max);
}

// Die Gravur-Vorschau skaliert nur die Breite (fitScale). Ohne Zeilenlimit
// waechst sie in der Hoehe aus dem Rahmen, sobald jemand lauter Umbrueche tippt.
export function clampLines(value, maxLines = 4) {
  const text = String(value ?? "");
  const lines = text.split("\n");
  return lines.length <= maxLines ? text : lines.slice(0, maxLines).join("\n");
}

export function insertSymbol(text, symbol, start, end, max = 30) {
  const base = String(text ?? "");
  const glyph = String(symbol ?? "");
  const length = base.length;
  const clampIndex = (value) => {
    const number = Number(value);
    return Number.isInteger(number) ? Math.min(length, Math.max(0, number)) : length;
  };
  let from = clampIndex(start);
  let to = clampIndex(end);
  if (from > to) [from, to] = [to, from];
  const result = clampText(base.slice(0, from) + glyph + base.slice(to), max);
  return { text: result, caret: Math.min(from + glyph.length, result.length) };
}

export function fitScale(contentWidth, availableWidth) {
  const content = Number(contentWidth);
  const available = Number(availableWidth);
  if (!Number.isFinite(content) || content <= 0 || !Number.isFinite(available) || available <= 0) return 1;
  return Math.max(0.1, Math.min(1, available / content));
}

export function clampTextPosition(position = {}, dimensions = {}) {
  const base = clampPosition(position);
  const stageWidth = Number(dimensions.stageWidth);
  const stageHeight = Number(dimensions.stageHeight);
  const textWidth = Number(dimensions.textWidth);
  const textHeight = Number(dimensions.textHeight);
  if (![stageWidth, stageHeight, textWidth, textHeight].every(Number.isFinite) || stageWidth <= 0 || stageHeight <= 0) {
    return base;
  }
  const halfWidth = Math.min(50, Math.max(0, (textWidth / stageWidth) * 50));
  const halfHeight = Math.min(50, Math.max(0, (textHeight / stageHeight) * 50));
  return {
    x: Math.round(Math.min(100 - halfWidth, Math.max(halfWidth, base.x))),
    y: Math.round(Math.min(100 - halfHeight, Math.max(halfHeight, base.y)))
  };
}

export function formatGravurPlatzierung({ x, y, size, fit = 1 } = {}) {
  return formatPlatzierung({ x, y, size: Number(size) * Number(fit) });
}

// Baut den menschenlesbaren Platzierungs-Wert für die Bestellung.
export function formatPlatzierung({ x, y, size } = {}) {
  const p = clampPosition({ x, y });
  const s = Number.isFinite(Number(size)) ? Math.round(Number(size)) : 0;
  return `Horizontal ${p.x} %, Vertikal ${p.y} %, Größe ${s} %`;
}

// Prüft, ob eine Datei ein akzeptiertes Bild in erlaubter Größe ist.
export function isImageFile(file, maxBytes = 20 * 1024 * 1024) {
  if (!file || typeof file.type !== "string") return false;
  if (!file.type.startsWith("image/")) return false;
  if (typeof file.size === "number" && file.size > maxBytes) return false;
  return true;
}

export function presentConfirmedMotif({
  detail,
  overlays = [],
  previousUrl = null,
  createObjectURL = file => URL.createObjectURL(file),
  revokeObjectURL = url => URL.revokeObjectURL(url)
} = {}) {
  const file = detail?.file;
  const width = Number(detail?.width);
  const height = Number(detail?.height);
  if (!file || !Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return { applied: false, objectUrl: previousUrl, file: null };
  }
  let objectUrl;
  try {
    objectUrl = createObjectURL(file);
  } catch {
    return { applied: false, objectUrl: previousUrl, file: null };
  }
  overlays.forEach((overlay) => {
    overlay.style.backgroundImage = `url("${objectUrl}")`;
    overlay.style.aspectRatio = `${width} / ${height}`;
  });
  if (previousUrl) revokeObjectURL(previousUrl);
  return { applied: true, objectUrl, file };
}

export function handleConfirmedMotifEvent({
  detail,
  currentFile,
  apply,
  onReset,
  onRemove
} = {}) {
  if (!detail?.file || detail.file !== currentFile) return "stale";
  let result;
  try {
    result = apply?.(detail);
  } catch {
    result = null;
  }
  if (result?.applied) return "applied";
  onReset?.({ showError: true });
  onRemove?.();
  return "reset";
}

export function activeModeForContent({ hasMotif = false, hasText = false, preferred = "motiv" } = {}) {
  if (preferred === "text" && hasText) return "text";
  if (preferred === "motiv" && hasMotif) return "motiv";
  if (hasText) return "text";
  if (hasMotif) return "motiv";
  return ["motiv", "text"].includes(preferred) ? preferred : "motiv";
}

export function motifFileTransition({ file, hasText = false } = {}) {
  if (!file) return { type: "none", showError: false, activeMode: activeModeForContent({ hasText }) };
  if (!isImageFile(file)) {
    return {
      type: "reset-motif",
      showError: true,
      activeMode: activeModeForContent({ hasText, preferred: hasText ? "text" : "motiv" })
    };
  }
  return { type: "show-motif", showError: false, activeMode: "motiv" };
}

// ── DOM-Wiring ────────────────────────────────────────────────────────────
export function initMotivKonfigurator(root) {
  if (root.dataset.motivKonfiguratorReady === "true") return;
  const stage = root.querySelector("[data-preview-stage]");
  const input = root.querySelector("[data-motiv-input]");
  // Bühnen- und mobile Inline-Vorschau teilen sich Position/Größe (Prozentwerte).
  const motifOverlays = Array.from(root.querySelectorAll("[data-motiv-overlay]"));
  const controls = root.querySelector("[data-motiv-controls]");
  const sizeSlider = root.querySelector("[data-motiv-size]");
  const removeBtn = root.querySelector("[data-motiv-remove]");
  const hint = root.querySelector("[data-motiv-hint]");
  const error = root.querySelector("[data-motiv-error]");
  const platzierungField = root.querySelector("[data-motiv-platzierung]");
  const personalization = root.querySelector("[data-personalisierung]");
  const modeButtons = Array.from(root.querySelectorAll("[data-personalisierung-mode]"));
  const textInput = root.querySelector("[data-box-gravur-input]");
  const textOverlays = Array.from(root.querySelectorAll("[data-box-gravur-overlay]"));
  const textSize = root.querySelector("[data-box-gravur-size]");
  const textRemove = root.querySelector("[data-box-gravur-remove]");
  const textPlacement = root.querySelector("[data-box-gravur-platzierung]");
  const symbolButtons = Array.from(root.querySelectorAll("[data-box-symbol]"));
  const productForm = root.querySelector("form[action*='/cart/add']");
  // Motiv- und Textgravur sind getrennt schaltbar: die Geschenkbox-Section rendert
  // nur den Textteil (kein Upload). Erst aussteigen, wenn beide Hälften fehlen.
  const hasMotifFeature = Boolean(input && motifOverlays.length && sizeSlider && platzierungField);
  const hasTextFeature = Boolean(textInput && textOverlays.length && textSize && textPlacement);
  if (!stage || (!hasMotifFeature && !hasTextFeature)) return;
  root.dataset.motivKonfiguratorReady = "true";
  const listeners = [];
  const listen = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);
    listeners.push(() => target.removeEventListener(type, handler, options));
  };

  let objectUrl = null;
  let pos = { x: 50, y: 50 }; // Prozent relativ zum jeweiligen Vorschau-Rahmen
  let textPos = { x: 50, y: 50 };
  let textFit = 1;
  let textMetrics = { width: 44, height: 44 };
  let hasMotif = false;
  let textLayoutFrame = null;
  let textLayoutFrameType = null;
  let textResizeObserver = null;
  let destroyed = false;

  function setActiveMode(mode) {
    if (!personalization || !["motiv", "text"].includes(mode)) return;
    personalization.classList.add("personalisierung--ready");
    personalization.dataset.activeMode = mode;
    modeButtons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.personalisierungMode === mode));
    });
    motifOverlays.forEach((overlay) => {
      const keyboardActive = stage.contains(overlay) && mode === "motiv" && !overlay.hidden;
      overlay.classList.toggle("motiv-overlay--active", mode === "motiv" && !overlay.hidden);
      overlay.tabIndex = keyboardActive ? 0 : -1;
    });
    textOverlays.forEach((overlay) => {
      const keyboardActive = stage.contains(overlay) && mode === "text" && !overlay.hidden;
      overlay.classList.toggle("box-gravur-overlay--active", mode === "text" && !overlay.hidden);
      overlay.tabIndex = keyboardActive ? 0 : -1;
    });
  }

  function updateStageHint() {
    if (!hint) return;
    const hasText = Boolean(textInput?.value.trim());
    hint.hidden = hasMotif || hasText;
  }

  function measureTextPosition() {
    const overlay = textOverlays.find((item) => (
      !item.hidden &&
      item.getClientRects().length > 0 &&
      item.parentElement?.clientWidth > 0 &&
      item.parentElement?.clientHeight > 0
    ));
    if (!overlay) return;
    const parent = overlay.parentElement;
    const baseSize = Number(textSize?.value) || 100;
    textOverlays.forEach((item) => {
      item.style.setProperty("--box-gravur-fit", "1");
      item.style.setProperty("--box-gravur-render-size", String(baseSize));
    });
    textFit = fitScale(overlay.scrollWidth, parent.clientWidth * 0.88);
    textOverlays.forEach((item) => {
      item.style.setProperty("--box-gravur-fit", String(textFit));
      item.style.setProperty("--box-gravur-render-size", String(baseSize * textFit));
    });
    textMetrics = { width: overlay.scrollWidth, height: overlay.scrollHeight };
    textPos = clampTextPosition(textPos, {
      stageWidth: parent.clientWidth,
      stageHeight: parent.clientHeight,
      textWidth: textMetrics.width,
      textHeight: textMetrics.height
    });
  }

  function updateTextPosition() {
    if (!textInput?.value.trim() || !textPlacement || !textSize) return;
    textOverlays.forEach((overlay) => {
      overlay.style.setProperty("--box-gravur-x", `${textPos.x}%`);
      overlay.style.setProperty("--box-gravur-y", `${textPos.y}%`);
    });
    textPlacement.value = formatGravurPlatzierung({
      x: textPos.x,
      y: textPos.y,
      size: textSize.value,
      fit: textFit
    });
  }

  function updateTextOverlay() {
    if (!textInput || !textSize || !textPlacement) return;
    textInput.value = clampLines(clampText(textInput.value, Number(textInput.maxLength) || 30));
    const hasText = textInput.value.trim() !== "";
    textOverlays.forEach((overlay) => {
      overlay.textContent = textInput.value;
      overlay.hidden = !hasText;
      overlay.style.setProperty("--box-gravur-x", `${textPos.x}%`);
      overlay.style.setProperty("--box-gravur-y", `${textPos.y}%`);
      overlay.style.setProperty("--box-gravur-size", textSize.value);
    });
    textPlacement.disabled = !hasText;
    if (!hasText) {
      textPlacement.value = "";
      updateStageHint();
      return;
    }
    measureTextPosition();
    updateTextPosition();
    updateStageHint();
  }

  function scheduleTextLayoutUpdate() {
    if (destroyed || !textInput?.value.trim() || textLayoutFrame !== null) return;
    const refresh = () => {
      textLayoutFrame = null;
      textLayoutFrameType = null;
      if (destroyed) return;
      updateTextOverlay();
      if (personalization?.dataset.activeMode === "text") setActiveMode("text");
    };
    if (typeof requestAnimationFrame === "function") {
      textLayoutFrameType = "animation";
      textLayoutFrame = requestAnimationFrame(refresh);
    } else {
      textLayoutFrameType = "timeout";
      textLayoutFrame = setTimeout(refresh, 0);
    }
  }

  function updateOverlay() {
    motifOverlays.forEach((overlay) => {
      overlay.style.setProperty("--motiv-x", pos.x + "%");
      overlay.style.setProperty("--motiv-y", pos.y + "%");
      overlay.style.setProperty("--motiv-size", sizeSlider.value + "%");
    });
    platzierungField.value = formatPlatzierung({ x: pos.x, y: pos.y, size: sizeSlider.value });
  }

  function showMotiv() {
    hasMotif = true;
    motifOverlays.forEach((overlay) => { overlay.hidden = false; });
    if (controls) controls.hidden = false;
    updateStageHint();
  }

  function resetMotiv({ showError = false, activeMode } = {}) {
    if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; }
    motifOverlays.forEach((overlay) => {
      overlay.style.backgroundImage = "";
      overlay.style.removeProperty("aspect-ratio");
      overlay.hidden = true;
    });
    if (controls) controls.hidden = true;
    hasMotif = false;
    input.value = "";
    platzierungField.value = "";
    pos = { x: 50, y: 50 };
    sizeSlider.value = sizeSlider.defaultValue;
    if (error) error.hidden = !showError;
    updateStageHint();
    setActiveMode(activeMode ?? activeModeForContent({
      hasMotif,
      hasText: Boolean(textInput?.value.trim()),
      preferred: personalization?.dataset.activeMode
    }));
  }

  function applyMotivFile(detail) {
    const preservePlacement = hasMotif;
    if (error) error.hidden = true;
    const presented = presentConfirmedMotif({
      detail,
      overlays: motifOverlays,
      previousUrl: objectUrl
    });
    if (!presented.applied) return presented;
    objectUrl = presented.objectUrl;
    if (!preservePlacement) {
      pos = { x: 50, y: 50 };
      sizeSlider.value = sizeSlider.defaultValue;
    }
    showMotiv();
    updateOverlay();
    setActiveMode("motiv");
    return presented;
  }

  const handleConfirmedCrop = event => {
    handleConfirmedMotifEvent({
      detail: event.detail,
      currentFile: input.files?.[0],
      apply: applyMotivFile,
      onReset: () => resetMotiv({ showError: true }),
      onRemove: () => root.dispatchEvent(new CustomEvent("motiv:entfernt"))
    });
  };
  // Alles hierin fasst input/sizeSlider/platzierungField an — ohne Motiv-Hälfte
  // gibt es diese Felder nicht.
  if (hasMotifFeature) {
    listen(root, "motiv:zuschnitt-bestaetigt", handleConfirmedCrop);

    listen(sizeSlider, "input", () => {
      setActiveMode("motiv");
      updateOverlay();
    });
    if (removeBtn) listen(removeBtn, "click", () => {
      resetMotiv();
      root.dispatchEvent(new CustomEvent("motiv:entfernt"));
    });
  }

  motifOverlays.forEach((overlay) => {
    let dragging = false;
    let dragRect = null;

    // Ziehen per Pointer (Maus + Touch vereinheitlicht); Koordinaten relativ
    // zum eigenen Rahmen (Bühne bzw. Inline-Vorschau), beide 1:1.
    listen(overlay, "pointerdown", (e) => {
      if (overlay.hidden) return;
      setActiveMode("motiv");
      dragging = true;
      dragRect = overlay.parentElement.getBoundingClientRect();
      overlay.setPointerCapture(e.pointerId);
      overlay.classList.add("motiv-overlay--dragging");
      e.preventDefault();
    });
    listen(overlay, "pointermove", (e) => {
      if (!dragging || !dragRect) return;
      const rect = dragRect;
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      pos = clampPosition({ x, y });
      updateOverlay();
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      dragRect = null;
      if (overlay.hasPointerCapture(e.pointerId)) overlay.releasePointerCapture(e.pointerId);
      overlay.classList.remove("motiv-overlay--dragging");
    }
    listen(overlay, "pointerup", endDrag);
    listen(overlay, "pointercancel", endDrag);

    // Tastatur-Nudge (Barrierefreiheit): Pfeiltasten verschieben, Shift = größere
    // Schritte. Greift nur auf der fokussierbaren Bühnen-Vorschau (tabindex).
    listen(overlay, "keydown", (e) => {
      if (personalization?.dataset.activeMode !== "motiv") return;
      const step = e.shiftKey ? 5 : 1;
      let handled = true;
      if (e.key === "ArrowLeft") pos = clampPosition({ x: pos.x - step, y: pos.y });
      else if (e.key === "ArrowRight") pos = clampPosition({ x: pos.x + step, y: pos.y });
      else if (e.key === "ArrowUp") pos = clampPosition({ x: pos.x, y: pos.y - step });
      else if (e.key === "ArrowDown") pos = clampPosition({ x: pos.x, y: pos.y + step });
      else handled = false;
      if (handled) { e.preventDefault(); updateOverlay(); }
    });
  });

  textOverlays.forEach((overlay) => {
    let dragging = false;
    let dragRect = null;
    listen(overlay, "pointerdown", (event) => {
      if (overlay.hidden) return;
      setActiveMode("text");
      dragging = true;
      dragRect = overlay.parentElement.getBoundingClientRect();
      overlay.setPointerCapture(event.pointerId);
      event.preventDefault();
    });
    listen(overlay, "pointermove", (event) => {
      if (!dragging || !dragRect) return;
      const rect = dragRect;
      textPos = clampTextPosition({
        x: ((event.clientX - rect.left) / rect.width) * 100,
        y: ((event.clientY - rect.top) / rect.height) * 100
      }, {
        stageWidth: rect.width,
        stageHeight: rect.height,
        textWidth: textMetrics.width,
        textHeight: textMetrics.height
      });
      updateTextPosition();
    });
    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      dragRect = null;
      if (overlay.hasPointerCapture(event.pointerId)) overlay.releasePointerCapture(event.pointerId);
    };
    listen(overlay, "pointerup", endDrag);
    listen(overlay, "pointercancel", endDrag);
    listen(overlay, "keydown", (event) => {
      if (personalization?.dataset.activeMode !== "text") return;
      const step = event.shiftKey ? 5 : 1;
      const movement = {
        ArrowLeft: { x: -step, y: 0 },
        ArrowRight: { x: step, y: 0 },
        ArrowUp: { x: 0, y: -step },
        ArrowDown: { x: 0, y: step }
      }[event.key];
      if (!movement) return;
      event.preventDefault();
      const parent = overlay.parentElement;
      textPos = clampTextPosition({ x: textPos.x + movement.x, y: textPos.y + movement.y }, {
        stageWidth: parent.clientWidth,
        stageHeight: parent.clientHeight,
        textWidth: textMetrics.width,
        textHeight: textMetrics.height
      });
      updateTextPosition();
    });
  });

  modeButtons.forEach((button) => listen(button, "click", () => setActiveMode(button.dataset.personalisierungMode)));
  if (textInput) listen(textInput, "input", () => {
    updateTextOverlay();
    setActiveMode(activeModeForContent({
      hasMotif,
      hasText: Boolean(textInput.value.trim()),
      preferred: "text"
    }));
  });
  if (textSize) listen(textSize, "input", () => { updateTextOverlay(); setActiveMode("text"); });
  symbolButtons.forEach((button) => listen(button, "click", () => {
    if (!textInput) return;
    const result = insertSymbol(textInput.value, button.dataset.boxSymbol, textInput.selectionStart, textInput.selectionEnd, 30);
    textInput.value = result.text;
    textInput.focus();
    textInput.setSelectionRange(result.caret, result.caret);
    updateTextOverlay();
    setActiveMode("text");
  }));
  if (textRemove) listen(textRemove, "click", () => {
    if (!textInput || !textSize) return;
    textInput.value = "";
    textPos = { x: 50, y: 50 };
    textSize.value = textSize.defaultValue;
    updateTextOverlay();
    setActiveMode(activeModeForContent({
      hasMotif,
      hasText: false,
      preferred: personalization?.dataset.activeMode
    }));
    textInput.focus();
  });
  if (productForm) listen(productForm, "submit", () => {
    if (!textInput || textInput.value.trim() !== "") return;
    textInput.disabled = true;
    if (textPlacement) textPlacement.disabled = true;
  });

  const previewContainers = [...new Set(textOverlays.map((overlay) => overlay.parentElement).filter(Boolean))];
  if (typeof ResizeObserver === "function") {
    textResizeObserver = new ResizeObserver((entries) => {
      if (entries.some((entry) => entry.target.clientWidth > 0 && entry.target.clientHeight > 0)) {
        scheduleTextLayoutUpdate();
      }
    });
    previewContainers.forEach((container) => textResizeObserver.observe(container));
  }
  if (typeof window !== "undefined") listen(window, "resize", scheduleTextLayoutUpdate);
  const fontsReady = document.fonts?.ready;
  if (fontsReady) fontsReady.then(scheduleTextLayoutUpdate).catch(() => {});

  function cleanupMotivKonfigurator() {
    destroyed = true;
    textResizeObserver?.disconnect();
    if (textLayoutFrame !== null) {
      if (textLayoutFrameType === "animation" && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(textLayoutFrame);
      } else {
        clearTimeout(textLayoutFrame);
      }
      textLayoutFrame = null;
      textLayoutFrameType = null;
    }
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
    listeners.splice(0).forEach(remove => remove());
    delete root.dataset.motivKonfiguratorReady;
  }

  updateTextOverlay();
  // Ohne Motiv-Hälfte gibt es nichts umzuschalten: der Textmodus muss aktiv sein,
  // sonst bleiben Drag und Tastatur-Nudging des Gravurtexts gesperrt.
  setActiveMode(activeModeForContent({
    hasMotif,
    hasText: Boolean(textInput?.value.trim()),
    preferred: hasMotifFeature ? "motiv" : "text"
  }));
  return cleanupMotivKonfigurator;
}

if (typeof document !== "undefined") {
  registerSectionLifecycle({
    documentLike: document,
    selector: "[data-product-configurator]",
    initialize: initMotivKonfigurator
  });
}
